import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, Plus } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Avatar } from "../components/Avatar";
import { ru } from "../locales/ru";

export default function Admin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [maxUses, setMaxUses] = useState(5);

  const load = useCallback(async (q = "") => {
    try {
      const { data } = await api.get("/admin/users", { params: { search: q } });
      setUsers(data);
    } catch {
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const toggleDriver = async (u) => {
    const roles = u.roles.includes("driver")
      ? u.roles.filter((r) => r !== "driver")
      : [...u.roles, "driver"];
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/roles`, { roles });
      setUsers(users.map((x) => (x.id === u.id ? data : x)));
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    }
  };

  const toggleBan = async (u) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/ban`, { banned: !u.banned });
      setUsers(users.map((x) => (x.id === u.id ? data : x)));
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    }
  };

  const createMultiInvite = async () => {
    try {
      const { data } = await api.post("/invites", { max_uses: Number(maxUses) || 1 });
      navigator.clipboard.writeText(data.link);
      toast.success(`${ru.admin.invitesCreated}: ${data.code} (${ru.invites.copied})`);
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    }
  };

  return (
    <div className="px-6 pt-8 pb-28" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="admin-back-button" onClick={() => navigate("/profile")} className="p-2.5 rounded-full bg-gray-100 active:scale-[0.95] transition-transform">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-extrabold tracking-tight">{ru.admin.title}</h1>
      </div>

      <div className="flex items-center gap-2 bg-gray-100 rounded-xl px-4 h-12 mb-4">
        <Search size={18} className="text-gray-400 shrink-0" />
        <input
          data-testid="admin-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={ru.admin.searchPlaceholder}
          className="bg-transparent outline-none w-full text-base"
        />
      </div>

      <div className="flex items-center gap-2 mb-6">
        <span className="text-sm text-gray-500">{ru.admin.multiInvite}:</span>
        <input
          data-testid="admin-invite-maxuses-input"
          type="number"
          min={1}
          max={1000}
          value={maxUses}
          onChange={(e) => setMaxUses(e.target.value)}
          className="w-16 h-9 px-2 bg-gray-100 rounded-lg text-sm outline-none text-center"
        />
        <button data-testid="admin-create-invite-button" onClick={createMultiInvite} className="flex items-center gap-1 text-sm font-semibold bg-black text-white px-3 h-9 rounded-full active:scale-[0.95] transition-transform">
          <Plus size={14} /> {ru.admin.createMultiInvite}
        </button>
      </div>

      {users.length === 0 && <p className="text-gray-400 text-sm" data-testid="admin-empty">{ru.admin.empty}</p>}
      <div className="divide-y divide-gray-100">
        {users.map((u) => (
          <div key={u.id} className="py-4 flex items-center gap-3" data-testid={`admin-user-row-${u.phone}`}>
            <Link to={`/users/${u.id}`}>
              <Avatar avatar={u.avatar} name={u.name} size={44} />
            </Link>
            <div className="min-w-0 flex-1">
              <p className={`font-semibold truncate ${u.banned ? "line-through text-gray-400" : ""}`}>{u.name}</p>
              <p className="text-xs text-gray-500">{u.phone}</p>
              <div className="flex gap-1 mt-0.5 flex-wrap">
                {u.roles.map((r) => (
                  <span key={r} className="text-[10px] font-semibold bg-gray-100 px-1.5 py-0.5 rounded-full">{ru.roles[r] || r}</span>
                ))}
                {u.banned && <span className="text-[10px] font-semibold bg-[#FF3B30] text-white px-1.5 py-0.5 rounded-full">{ru.roles.banned}</span>}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 items-end shrink-0">
              <button
                data-testid={`toggle-driver-${u.phone}`}
                onClick={() => toggleDriver(u)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full active:scale-[0.95] transition-transform ${
                  u.roles.includes("driver") ? "bg-black text-white" : "bg-gray-100 text-gray-600"
                }`}
              >
                {ru.admin.driverToggle}
              </button>
              {!u.roles.includes("admin") && (
                <button
                  data-testid={`toggle-ban-${u.phone}`}
                  onClick={() => toggleBan(u)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full active:scale-[0.95] transition-transform ${
                    u.banned ? "bg-[#34C759] text-white" : "bg-[#FF3B30]/10 text-[#FF3B30]"
                  }`}
                >
                  {u.banned ? ru.admin.unban : ru.admin.ban}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
